import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  FormGroup,
  Button,
  Badge,
} from "reactstrap";
import PropTypes from "prop-types";
import { InkSelectionModal } from "../../components/InkSelectionModal";
import { RepositoryFactory } from "../../repositories";
import { ParametrosTintaExpanded } from "../../types/collections";

/**
 * Componente para el formulario de Planeación en una orden de producción
 * 
 * @param {Object} props - Props del componente
 * @param {Object} props.data - Datos de la Planeación
 * @param {Function} props.onChange - Handler para cambios en el formulario
 * @param {Array} props.medidas - Lista de medidas disponibles
 * @param {Array} props.tiposPinza - Lista de tipos de pinza disponibles
 * @returns {React.Component}
 */
const Planeacion = ({ data, onChange, medidas = [], tiposPinza = [] }) => {
  // State for ink selection modal
  const [isInkModalOpen, setIsInkModalOpen] = useState(false);
  const [parametrosTinta, setParametrosTinta] = useState([]);
  const [selectedParametros, setSelectedParametros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Repository for parametros_tinta
  const parametrosRepository = RepositoryFactory.getRepository('parametros_tinta');

  // Load parametros_tinta when component mounts
  const loadParametrosTinta = async () => {
    try {
      setLoading(true);
      setError(null);
      const parametros = await parametrosRepository.getAllActive();
      setParametrosTinta(parametros);
    } catch (err) {
      console.error('Error loading parametros_tinta:', err);
      setError('Error al cargar los parámetros de tinta');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadParametrosTinta();
  }, [parametrosRepository]);

  // Update selectedParametros when data.parametros_tinta changes
  useEffect(() => {
    if (data.parametros_tinta && Array.isArray(data.parametros_tinta)) {
      // Find the actual parametros objects from the loaded data
      const selected = parametrosTinta.filter(p => 
        data.parametros_tinta.includes(p.id)
      );
      setSelectedParametros(selected);
    } else {
      setSelectedParametros([]);
    }
  }, [data.parametros_tinta, parametrosTinta]);

  // Use React.useEffect to show a warning if medidas is empty but we have a planeacion_tamano
  React.useEffect(() => {
    if (medidas.length === 0 && data.planeacion_tamano) {
      console.warn("Warning: Planeacion component has a planeacion_tamano value but medidas array is empty.");
      console.warn("This may cause display issues with the 'Tamaño de Papel' select field.");
      console.warn("Current planeacion_tamano:", data.planeacion_tamano);
    }
  }, [medidas.length, data.planeacion_tamano]);
  // Handler para manejar cambios en los campos del formulario
  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    
    // Tratamiento especial para numero_troquel para evitar problemas de conversión
    if (name === "planeacion_numero_troquel") {
      // Asegurarnos de que es un número válido, sin caracteres extraños
      const cleanValue = value ? parseInt(value, 10) : "";
      onChange({
        target: {
          name,
          value: cleanValue,
        },
      });
      return;
    }
    
    onChange({
      target: {
        name,
        value: type === "checkbox" || type === "switch" ? checked : value,
      },
    });
  };

  // Handler para abrir el modal de selección de tintas
  const handleOpenInkModal = () => {
    setIsInkModalOpen(true);
  };

  // Handler para cerrar el modal de selección de tintas
  const handleCloseInkModal = () => {
    setIsInkModalOpen(false);
  };

  // Handler para confirmar la selección de tintas
  const handleConfirmInkSelection = (selectedParametros) => {
    setSelectedParametros(selectedParametros);
    
    // Notify parent component about the change
    onChange({
      target: {
        name: 'parametros_tinta',
        value: selectedParametros.map(p => p.id),
      },
    });
    
    setIsInkModalOpen(false);
  };

  // Handler para recargar los parámetros de tinta
  const handleReloadParametrosTinta = () => {
    loadParametrosTinta();
  };

  return (
    <>
    <Card className="mb-3 border border-4 border-light">
      <CardBody>
        <h4 className="card-title mb-3">Planeación</h4>
        
        {/* Tamaño Abierto */}
        <Row className="mb-3">
          <Col md="12">
            <h5>Tamaño Abierto</h5>
          </Col>
          <Col md="6">
            <div className="mb-3">
              <Label className="form-label" htmlFor="planeacion_abierto_ancho">Ancho (cms)</Label>
              <Input
                type="number"
                className="form-control"
                id="planeacion_abierto_ancho"
                name="planeacion_abierto_ancho"
                value={data.planeacion_abierto_ancho || ""}
                onChange={handleChange}
              />
            </div>
          </Col>
          <Col md="6">
            <div className="mb-3">
              <Label className="form-label" htmlFor="planeacion_abierto_alto">Alto (cms)</Label>
              <Input
                type="number"
                className="form-control"
                id="planeacion_abierto_alto"
                name="planeacion_abierto_alto"
                value={data.planeacion_abierto_alto || ""}
                onChange={handleChange}
              />
            </div>
          </Col>
        </Row>
        
        {/* Tamaño Armado */}
        <Row className="mb-3">
          <Col md="12">
            <h5>Tamaño Armado</h5>
          </Col>
          <Col md="4">
            <div className="mb-3">
              <Label className="form-label" htmlFor="planeacion_armado_ancho">Ancho (cms)</Label>
              <Input
                type="number"
                className="form-control"
                id="planeacion_armado_ancho"
                name="planeacion_armado_ancho"
                value={data.planeacion_armado_ancho || ""}
                onChange={handleChange}
              />
            </div>
          </Col>
          <Col md="4">
            <div className="mb-3">
              <Label className="form-label" htmlFor="planeacion_armado_alto">Alto (cms)</Label>
              <Input
                type="number"
                className="form-control"
                id="planeacion_armado_alto"
                name="planeacion_armado_alto"
                value={data.planeacion_armado_alto || ""}
                onChange={handleChange}
              />
            </div>
          </Col>
          <Col md="4">
            <div className="mb-3">
              <Label className="form-label" htmlFor="planeacion_armado_prof">Profundidad (cms)</Label>
              <Input
                type="number"
                className="form-control"
                id="planeacion_armado_prof"
                name="planeacion_armado_prof"
                value={data.planeacion_armado_prof || ""}
                onChange={handleChange}
              />
            </div>
          </Col>
        </Row>
        
        {/* Tintas */}
        <Row className="mb-3">
          <Col md="12">
            <div className="mb-3">
              <Label className="form-label">Tintas</Label>
              <div className="d-flex align-items-center gap-2">
                <Button
                  color="outline-primary"
                  onClick={handleOpenInkModal}
                  disabled={loading}
                  className="d-flex align-items-center"
                >
                  <i className="fas fa-palette me-2"></i>
                  Seleccionar Tintas
                </Button>
                {selectedParametros.length > 0 ? (
                  <div className="ms-2">
                    <Badge color="info" className="me-2">
                      {selectedParametros.length} parámetro{selectedParametros.length !== 1 ? 's' : ''} seleccionado{selectedParametros.length !== 1 ? 's' : ''}
                    </Badge>
                    <div className="mt-2">
                      <small className="text-muted">Seleccionados:</small>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {selectedParametros.map((parametro) => (
                          <Badge key={parametro.id} color="primary" className="me-1 mb-1">
                            {parametro.nombre}
                            {parametro.expand?.tinta && Array.isArray(parametro.expand.tinta) && parametro.expand.tinta.length > 0 && (
                              <span className="ms-1">
                                ({(() => {
                                  const order = ['C', 'M', 'Y', 'K'];
                                  return parametro.expand.tinta
                                    .sort((a, b) => {
                                      const aIndex = order.indexOf(a.nombre);
                                      const bIndex = order.indexOf(b.nombre);
                                      if (aIndex !== -1 && bIndex !== -1) { return aIndex - bIndex; }
                                      if (aIndex !== -1) return -1;
                                      if (bIndex !== -1) return 1;
                                      return a.nombre.localeCompare(b.nombre);
                                    })
                                    .map(tinta => tinta.nombre)
                                    .join(', ');
                                })()})
                              </span>
                            )}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <span className="text-muted ms-2">Ningún parámetro seleccionado</span>
                )}
              </div>
              {error && (
                <div className="text-danger mt-2">
                  <i className="fas fa-exclamation-triangle me-1"></i>
                  {error}
                </div>
              )}
            </div>
          </Col>
        </Row>
        
        {/* Detalles técnicos */}
        <Row className="mb-3">
          <Col md="6">
            <div className="mb-3">
              <Label className="form-label" htmlFor="planeacion_lineatura">Lineatura</Label>
              <Input
                type="text"
                className="form-control"
                id="planeacion_lineatura"
                name="planeacion_lineatura"
                value={data.planeacion_lineatura || ""}
                onChange={handleChange}
                placeholder="Ej: 150 LPI"
              />
            </div>
          </Col>
          <Col md="6">
            <div className="mb-3">
              <Label className="form-label" htmlFor="planeacion_tipo_pinza">Tipo de Pinza</Label>
              <select
                className="form-select form-select-lg"
                id="planeacion_tipo_pinza"
                name="planeacion_tipo_pinza"
                value={data.planeacion_tipo_pinza || ""}
                onChange={handleChange}
              >
                <option value="">Seleccionar tipo de pinza...</option>
                {Array.isArray(tiposPinza) && tiposPinza.length > 0 ? (
                  tiposPinza.map((tipoPinza) => (
                    <option key={`planeacion-tipo-pinza-${tipoPinza.id}`} value={tipoPinza.id}>
                      {tipoPinza.nombre}
                    </option>
                  ))
                ) : data.planeacion_tipo_pinza ? (
                  // If tiposPinza is empty but we have a selected value, add a placeholder option
                  <option value={data.planeacion_tipo_pinza}>
                    Cargando tipo de pinza... (ID: {data.planeacion_tipo_pinza})
                  </option>
                ) : null}
              </select>
            </div>
          </Col>
        </Row>
        
        {/* Troquel */}
        <Row className="mb-3">
          <Col md="12">
            <h5>Troquel</h5>
          </Col>
          <Col md="3">
            <FormGroup>
              <div className="form-check form-switch">
                <Input
                  type="switch"
                  className="form-check-input"
                  id="planeacion_troquel_n"
                  name="planeacion_troquel_n"
                  checked={data.planeacion_troquel_n || false}
                  onChange={handleChange}
                />
                <Label className="form-check-label" htmlFor="planeacion_troquel_n">
                  Nuevo
                </Label>
              </div>
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <div className="form-check form-switch">
                <Input
                  type="switch"
                  className="form-check-input"
                  id="planeacion_troquel_e"
                  name="planeacion_troquel_e"
                  checked={data.planeacion_troquel_e || false}
                  onChange={handleChange}
                />
                <Label className="form-check-label" htmlFor="planeacion_troquel_e">
                  Existente
                </Label>
              </div>
            </FormGroup>
          </Col>
          <Col md="6">
            <div className="mb-3">
              <Label className="form-label" htmlFor="planeacion_numero_troquel">Número de Troquel</Label>
              <Input
                type="number" 
                className="form-control"
                id="planeacion_numero_troquel"
                name="planeacion_numero_troquel"
                value={data.planeacion_numero_troquel || ""}
                onChange={handleChange}
                min="0"
                step="1"
                pattern="[0-9]*" 
                inputMode="numeric"
                onBlur={(e) => {
                  // Limpiar el valor al perder el foco
                  if (e.target.value) {
                    const cleanValue = parseInt(e.target.value, 10);
                    onChange({
                      target: {
                        name: "planeacion_numero_troquel",
                        value: cleanValue
                      }
                    });
                  }
                }}
              />
            </div>
          </Col>
        </Row>
        
        {/* Tamaño y Cabida */}
        <Row className="mb-3">
          <Col md="6">
            <div className="mb-3">
              <Label className="form-label" htmlFor="planeacion_tamano">Tamaño de Papel</Label>
              <select
                className="form-select form-select-lg"
                id="planeacion_tamano"
                name="planeacion_tamano"
                value={data.planeacion_tamano || ""}
                onChange={handleChange}
              >
                <option value="">Seleccionar...</option>
                {Array.isArray(medidas) && medidas.length > 0 ? (
                  medidas.map((medida) => (
                    <option key={`planeacion-${medida.id}`} value={medida.id}>
                      {medida.nombre}
                    </option>
                  ))
                ) : data.planeacion_tamano ? (
                  // If medidas is empty but we have a selected value, add a placeholder option
                  <option value={data.planeacion_tamano}>
                    Cargando medida... (ID: {data.planeacion_tamano})
                  </option>
                ) : null}
              </select>
            </div>
          </Col>
          <Col md="6">
            <div className="mb-3">
              <Label className="form-label" htmlFor="planeacion_cabida_preprensa">Cabida Preprensa</Label>
              <Input
                type="text"
                className="form-control"
                id="planeacion_cabida_preprensa"
                name="planeacion_cabida_preprensa"
                value={data.planeacion_cabida_preprensa || ""}
                onChange={handleChange}
                placeholder="Ej: 8"
              />
            </div>
          </Col>
        </Row>
        

      </CardBody>
    </Card>

    {/* Ink Selection Modal */}
    <InkSelectionModal
      isOpen={isInkModalOpen}
      onClose={handleCloseInkModal}
      onConfirm={handleConfirmInkSelection}
      selectedParametros={selectedParametros}
      parametros={parametrosTinta}
      loading={loading}
      error={error}
      onReload={handleReloadParametrosTinta}
    />
    </>
  );
};

Planeacion.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  medidas: PropTypes.array.isRequired,
  tiposPinza: PropTypes.array.isRequired
};

export default Planeacion;
